import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog } from '../ui';
import { archiveProject } from '../../lib/api/projects';

/**
 * Project-level destructive actions card. Currently has one action —
 * archive the project (soft-delete: the DDB rows are preserved; the UI
 * just stops showing the project).
 *
 * Guarded by a confirmation modal that requires the operator to type
 * the project id so it can't fire on a stray click.
 */
export const DangerZone: React.FC<{ projectId: string; projectName: string }> = ({
  projectId,
  projectName,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white border border-danger-200 rounded-xl shadow-card overflow-hidden" data-testid="danger-zone">
      <header className="px-5 py-3 bg-danger-50 border-b border-danger-200">
        <h2 className="text-sm font-semibold text-danger-700">Danger zone</h2>
      </header>
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="text-sm text-slate-700">
          <div className="font-medium text-slate-900">Delete this project</div>
          <p className="text-xs text-slate-600 mt-1 max-w-prose">
            Archives the project so it stops appearing across the portal. Settings, repos,
            guardrails, and audit history stay in the database so an operator can
            restore the project later if needed.
          </p>
        </div>
        <Button variant="danger" size="md" onClick={() => setOpen(true)} data-testid="danger-zone-delete">
          Delete project
        </Button>
      </div>
      {open && (
        <ConfirmDeleteModal
          projectId={projectId}
          projectName={projectName}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
};

const ConfirmDeleteModal: React.FC<{
  projectId: string;
  projectName: string;
  onClose: () => void;
}> = ({ projectId, projectName, onClose }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');

  const archive = useMutation({
    mutationFn: () => archiveProject(projectId),
    onSuccess: () => {
      // Drop every cached project query so the list re-fetches without
      // the archived row, then bounce to the projects list.
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      navigate('/projects', { replace: true });
    },
  });

  const matches = typed.trim() === projectId;

  return (
    <ConfirmDialog
      open
      title={`Delete ${projectName}?`}
      description="The project will be archived and removed from every list. Settings, repos, guardrails, and audit history are preserved."
      confirmLabel="Delete project"
      tone="danger"
      pending={archive.isPending}
      confirmDisabled={!matches}
      onConfirm={() => archive.mutate()}
      onCancel={onClose}
      testId="danger-zone-confirm-submit"
    >
      <div className="space-y-1.5">
        <label htmlFor="confirm-project-id" className="block text-xs text-slate-600">
          Type <span className="font-mono text-slate-900">{projectId}</span> to confirm
        </label>
        <input
          id="confirm-project-id"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={projectId}
          autoComplete="off"
          data-testid="danger-zone-confirm-input"
          className="block w-full border border-slate-300 rounded-md text-sm px-2.5 py-1.5 font-mono"
        />
      </div>

      {archive.error && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2 mt-3">
          {(archive.error as Error).message}
        </div>
      )}
    </ConfirmDialog>
  );
};

export default DangerZone;
